import { AyurmaStrings } from "../i18n/ayurma";

type MetricKey = keyof AyurmaStrings["metrics"];

/**
 * Maps a metric's METRIC_TYPE code onto its entry in the dictionary.
 *
 * METRIC_TYPE is the language-independent identifier — TITLE1 is localized text and
 * so cannot be matched on. Codes come from `reports/constants.js`; two of the enum
 * names there are legacy misnomers for the RMR context: 25 is `SDNN` but presents as
 * Sympathetic/Parasympathetic activation, and 8 is `METABOLIC_DYSFUNCTION_RISK` but
 * presents as Metabolic flexibility — the risk metric, which lives outside the
 * METRICS arrays under ORGANS.<organ>.RISK_METRICS and renders as a Low-to-High
 * range bar rather than a five-band score bar.
 */
export const METRIC_KEYS: Record<number, MetricKey> = {
  2: "metabolicRate",
  3: "fatBurningEfficiency",
  4: "heartRateVariability",
  5: "lungUtilization",
  8: "metabolicFlexibility",
  13: "respiratoryCapacity",
  19: "breathingCoordination",
  24: "oxygenDelivery",
  25: "sympatheticParasympathetic",
  26: "ventilationEfficiency",
};

const entry = (
  metricType: number | undefined,
  metrics: AyurmaStrings["metrics"]
) => {
  const key = metricType == null ? undefined : METRIC_KEYS[metricType];
  return key ? metrics[key] : undefined;
};

/**
 * Localized metric name, falling back to the payload's own title so an unmapped
 * metric still renders (e.g. the extra metrics on a Frank Shallenberger report)
 * rather than disappearing.
 */
export const metricName = (
  metricType: number | undefined,
  metrics: AyurmaStrings["metrics"],
  fallbackTitle?: string
): string => entry(metricType, metrics)?.name ?? fallbackTitle ?? "";

/**
 * Localized explainer, or undefined when the copy has not been written yet — the
 * dictionary uses an empty string for that, and the score bar omits the line.
 */
export const metricDescription = (
  metricType: number | undefined,
  metrics: AyurmaStrings["metrics"]
): string | undefined => entry(metricType, metrics)?.description || undefined;
